// File: src/context/LanguageContext.js
// Description: Language context for bilingual support with comprehensive features

import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';

// Language constants
export const LANGUAGES = {
  EN: 'en',
  MM: 'my'
};

// Storage keys
export const STORAGE_KEYS = {
  LANGUAGE: 'mahavihara_language',
  LANGUAGE_PREFERENCES: 'mahavihara_language_preferences'
};

// Storage utility
const storage = {
  get: (key) => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      console.error('Storage get error:', error);
      return null;
    }
  },
  set: (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error('Storage set error:', error);
    }
  },
  remove: (key) => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Storage remove error:', error);
    }
  }
};

// Initial state
const initialState = {
  currentLanguage: LANGUAGES.EN,
  isLoading: false,
  translations: {},
  isRTL: false,
  fontLoaded: false,
  preferences: {
    autoDetect: true,
    fallbackLanguage: LANGUAGES.EN,
    persistLanguage: true,
  }
};

// Action types
const LANGUAGE_ACTIONS = {
  SET_LANGUAGE: 'SET_LANGUAGE',
  SET_LOADING: 'SET_LOADING',
  SET_TRANSLATIONS: 'SET_TRANSLATIONS',
  SET_RTL: 'SET_RTL',
  SET_FONT_LOADED: 'SET_FONT_LOADED',
  SET_PREFERENCES: 'SET_PREFERENCES',
  RESET_LANGUAGE: 'RESET_LANGUAGE',
};

// Reducer
const languageReducer = (state, action) => {
  switch (action.type) {
    case LANGUAGE_ACTIONS.SET_LANGUAGE:
      return {
        ...state,
        currentLanguage: action.payload,
        isRTL: action.payload === 'ar' || action.payload === 'fa', // For future RTL support
      };
    
    case LANGUAGE_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      };
    
    case LANGUAGE_ACTIONS.SET_TRANSLATIONS:
      return {
        ...state,
        translations: {
          ...state.translations,
          [action.payload.language]: action.payload.translations,
        },
      };
    
    case LANGUAGE_ACTIONS.SET_RTL:
      return {
        ...state,
        isRTL: action.payload,
      };
    
    case LANGUAGE_ACTIONS.SET_FONT_LOADED:
      return {
        ...state,
        fontLoaded: action.payload,
      };
    
    case LANGUAGE_ACTIONS.SET_PREFERENCES:
      return {
        ...state,
        preferences: {
          ...state.preferences,
          ...action.payload,
        },
      };
    
    case LANGUAGE_ACTIONS.RESET_LANGUAGE:
      return {
        ...initialState,
        preferences: state.preferences,
      };
    
    default:
      return state;
  }
};

// Create context
const LanguageContext = createContext();

// Default translations for common UI elements
const DEFAULT_TRANSLATIONS = {
  [LANGUAGES.EN]: {
    // Common UI
    loading: 'Loading...',
    error: 'Error',
    success: 'Success',
    warning: 'Warning',
    info: 'Information',
    
    // Actions
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    view: 'View',
    close: 'Close',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    submit: 'Submit',
    reset: 'Reset',
    search: 'Search',
    filter: 'Filter',
    sort: 'Sort',
    
    // Navigation
    home: 'Home',
    about: 'About',
    contact: 'Contact',
    login: 'Login',
    logout: 'Logout',
    register: 'Register',
    profile: 'Profile',
    settings: 'Settings',
    
    // University Navigation
    academic: 'Academic',
    foundation: 'Foundation',
    gallery: 'Gallery',
    library: 'Library',
    readingRoom: 'Reading Room',
    faculties: 'Faculties',
    notices: 'Notices',
    activities: 'Activities',
    donation: 'Donation',
    
    // Forms
    name: 'Name',
    email: 'Email',
    phone: 'Phone',
    address: 'Address',
    message: 'Message',
    password: 'Password',
    confirmPassword: 'Confirm Password',
    
    // Time
    today: 'Today',
    yesterday: 'Yesterday',
    tomorrow: 'Tomorrow',
    thisWeek: 'This Week',
    thisMonth: 'This Month',
    thisYear: 'This Year',
    
    // Status
    active: 'Active',
    inactive: 'Inactive',
    pending: 'Pending',
    approved: 'Approved',
    rejected: 'Rejected',
    completed: 'Completed',
    
    // Pagination
    page: 'Page',
    of: 'of',
    showing: 'Showing',
    results: 'results',
    noResults: 'No results found',
    
    // File operations
    upload: 'Upload',
    download: 'Download',
    preview: 'Preview',
    fileSize: 'File Size',
    fileName: 'File Name',
    
    // Reading Room specific
    readOnline: 'Read Online',
    categories: 'Categories',
    author: 'Author',
    publishDate: 'Publish Date',
    description: 'Description',
    allCategories: 'All Categories',
    
    // University specific
    university: 'Mahavihara University',
    faculty: 'Faculty',
    department: 'Department',
    course: 'Course',
    student: 'Student',
    teacher: 'Teacher',
    registrar: 'Registrar',
    offeringDegrees: 'Offering Degrees',
    currentActivities: 'Current Activities',
    members: 'Members',
    
    // Buddhist terms
    dhamma: 'Dhamma',
    buddha: 'Buddha',
    sangha: 'Sangha',
    meditation: 'Meditation',
    teaching: 'Teaching',
    monastery: 'Monastery',
    monk: 'Monk',
    nun: 'Nun',
    mahavihara: 'Mahavihara',
    
    // Common phrases
    learnMore: 'Learn More',
    readMore: 'Read More',
    viewAll: 'View All',
    getStarted: 'Get Started',
    contactUs: 'Contact Us',
    joinUs: 'Join Us',
    welcome: 'Welcome',
    thankYou: 'Thank You',
  },
  
  [LANGUAGES.MM]: {
    // Common UI
    loading: 'ဖွင့်နေသည်...',
    error: 'အမှား',
    success: 'အောင်မြင်',
    warning: 'သတိပေးချက်',
    info: 'အချက်အလက်',
    
    // Actions
    save: 'သိမ်းဆည်းရန်',
    cancel: 'ပယ်ဖျက်ရန်',
    delete: 'ဖျက်ရန်',
    edit: 'တည်းဖြတ်ရန်',
    view: 'ကြည့်ရှုရန်',
    close: 'ပိတ်ရန်',
    back: 'နောက်သို့',
    next: 'ရှေ့သို့',
    previous: 'ယခင်',
    submit: 'တင်သွင်းရန်',
    reset: 'ပြန်လည်သတ်မှတ်ရန်',
    search: 'ရှာဖွေရန်',
    filter: 'စစ်ထုတ်ရန်',
    sort: 'စီစဉ်ရန်',
    
    // Navigation
    home: 'ပင်မ',
    about: 'အကြောင်း',
    contact: 'ဆက်သွယ်ရန်',
    login: 'ဝင်ရောက်ရန်',
    logout: 'ထွက်ရန်',
    register: 'မှတ်ပုံတင်ရန်',
    profile: 'ကိုယ်ရေးအချက်အလက်',
    settings: 'ဆက်တင်များ',
    
    // University Navigation
    academic: 'ပညာရေး',
    foundation: 'တည်ထောင်ခြင်း',
    gallery: 'ပုံများ',
    library: 'စာကြည့်တိုက်',
    readingRoom: 'စာဖတ်ခန်း',
    faculties: 'ဌာနများ',
    notices: 'ကြေညာချက်များ',
    activities: 'လှုပ်ရှားမှုများ',
    donation: 'လှူဒါန်း',
    
    // Forms
    name: 'အမည်',
    email: 'အီးမေးလ်',
    phone: 'ဖုန်းနံပါတ်',
    address: 'လိပ်စာ',
    message: 'စာတိုက်',
    password: 'စကားဝှက်',
    confirmPassword: 'စကားဝှက် အတည်ပြုရန်',
    
    // Time
    today: 'ယနေ့',
    yesterday: 'မနေ့က',
    tomorrow: 'မနက်ဖြန်',
    thisWeek: 'ဤအပတ်',
    thisMonth: 'ဤလ',
    thisYear: 'ဤနှစ်',
    
    // Status
    active: 'အသုံးပြုနေသော',
    inactive: 'အသုံးမပြုသော',
    pending: 'စောင့်ဆိုင်းနေသော',
    approved: 'အတည်ပြုပြီး',
    rejected: 'ငြင်းပယ်ပြီး',
    completed: 'ပြီးစီးပြီး',
    
    // Pagination
    page: 'စာမျက်နှာ',
    of: 'မှ',
    showing: 'ပြသနေသည်',
    results: 'ရလဒ်များ',
    noResults: 'ရလဒ်များ မတွေ့ရှိပါ',
    
    // File operations
    upload: 'တင်ရန်',
    download: 'ဒေါင်းလုဒ်',
    preview: 'ကြိုကြည့်ရန်',
    fileSize: 'ဖိုင်အရွယ်အစား',
    fileName: 'ဖိုင်အမည်',
    
    // Reading Room specific
    readOnline: 'အွန်လိုင်းဖတ်ရှုရန်',
    categories: 'အမျိုးအစားများ',
    author: 'စာရေးသူ',
    publishDate: 'ထုတ်ဝေသည့်ရက်',
    description: 'ဖော်ပြချက်',
    allCategories: 'အမျိုးအစားအားလုံး',
    
    // University specific
    university: 'မဟာဝိဟာရ တက္ကသိုလ်',
    faculty: 'ဌာန',
    department: 'ဌာနခွဲ',
    course: 'သင်တန်း',
    student: 'ကျောင်းသား',
    teacher: 'ဆရာ',
    registrar: 'မှတ်ပုံတင်ရေးမှူး',
    offeringDegrees: 'ပေးအပ်သော ဘွဲ့များ',
    currentActivities: 'လက်ရှိ လှုပ်ရှားမှုများ',
    members: 'အဖွဲ့ဝင်များ',
    
    // Buddhist terms
    dhamma: 'ဓမ္မ',
    buddha: 'ဗုဒ္ဓ',
    sangha: 'သံဃာ',
    meditation: 'တရားထိုင်ခြင်း',
    teaching: 'ဆုံးမ',
    monastery: 'ကျောင်းတိုက်',
    monk: 'ရဟန်း',
    nun: 'ရဟန်းမ',
    mahavihara: 'မဟာဝိဟာရ',
    
    // Common phrases
    learnMore: 'ပိုမိုလေ့လာရန်',
    readMore: 'ပိုမိုဖတ်ရှုရန်',
    viewAll: 'အားလုံးကြည့်ရှုရန်',
    getStarted: 'စတင်ရန်',
    contactUs: 'ကျွန်ုပ်တို့ကို ဆက်သွယ်ရန်',
    joinUs: 'ကျွန်ုပ်တို့နှင့် ပူးပေါင်းရန်',
    welcome: 'ကြိုဆိုပါသည်',
    thankYou: 'ကျေးဇူးတင်ပါသည်',
  }
};

// Language Provider Component
export const LanguageProvider = ({ children }) => {
  const [state, dispatch] = useReducer(languageReducer, initialState);
  
  // Load saved language and preferences on mount
  useEffect(() => {
    const initializeLanguage = async () => {
      try {
        dispatch({ type: LANGUAGE_ACTIONS.SET_LOADING, payload: true });
        
        // Load saved preferences
        const savedPreferences = storage.get(STORAGE_KEYS.LANGUAGE_PREFERENCES);
        if (savedPreferences) {
          dispatch({
            type: LANGUAGE_ACTIONS.SET_PREFERENCES,
            payload: savedPreferences,
          });
        }
        
        // Load saved language
        const savedLanguage = storage.get(STORAGE_KEYS.LANGUAGE);
        let languageToUse = LANGUAGES.EN;
        
        if (savedLanguage && Object.values(LANGUAGES).includes(savedLanguage)) {
          languageToUse = savedLanguage;
        } else if (state.preferences.autoDetect) {
          // Auto-detect browser language
          const browserLanguage = navigator.language || navigator.userLanguage;
          if (browserLanguage.startsWith('my')) {
            languageToUse = LANGUAGES.MM;
          }
        }
        
        // Set default translations
        dispatch({
          type: LANGUAGE_ACTIONS.SET_TRANSLATIONS,
          payload: {
            language: LANGUAGES.EN,
            translations: DEFAULT_TRANSLATIONS[LANGUAGES.EN],
          },
        });
        
        dispatch({
          type: LANGUAGE_ACTIONS.SET_TRANSLATIONS,
          payload: {
            language: LANGUAGES.MM,
            translations: DEFAULT_TRANSLATIONS[LANGUAGES.MM],
          },
        });
        
        // Set the language
        dispatch({
          type: LANGUAGE_ACTIONS.SET_LANGUAGE,
          payload: languageToUse,
        });
        
        // Load Myanmar fonts if needed
        if (languageToUse === LANGUAGES.MM) {
          await loadMyanmarFonts();
        }
        
        dispatch({ type: LANGUAGE_ACTIONS.SET_LOADING, payload: false });
      } catch (error) {
        console.error('Language initialization error:', error);
        dispatch({ type: LANGUAGE_ACTIONS.SET_LOADING, payload: false });
      }
    };

    initializeLanguage();
  }, []);
  
  // Load Myanmar fonts
  const loadMyanmarFonts = useCallback(async () => {
    try {
      // Check if fonts are already loaded
      if (document.fonts && document.fonts.check) {
        const isFontLoaded = document.fonts.check('16px "Pyidaungsu"') ||
                           document.fonts.check('16px "Myanmar3"') ||
                           document.fonts.check('16px "Padauk"');
        
        if (isFontLoaded) {
          dispatch({ type: LANGUAGE_ACTIONS.SET_FONT_LOADED, payload: true });
          return;
        }
      }
      
      // For web fonts, we'll rely on CSS @font-face declarations
      // This is a fallback check
      const testElement = document.createElement('div');
      testElement.style.fontFamily = 'Pyidaungsu, Myanmar3, Padauk';
      testElement.style.position = 'absolute';
      testElement.style.visibility = 'hidden';
      testElement.innerHTML = 'မြန်မာ';
      document.body.appendChild(testElement);
      
      // Simple font load detection
      setTimeout(() => {
        document.body.removeChild(testElement);
        dispatch({ type: LANGUAGE_ACTIONS.SET_FONT_LOADED, payload: true });
      }, 100);
      
    } catch (error) {
      console.error('Font loading error:', error);
      dispatch({ type: LANGUAGE_ACTIONS.SET_FONT_LOADED, payload: true }); // Continue anyway
    }
  }, []);
  
  // Save language and preferences when changed
  useEffect(() => {
    if (state.preferences.persistLanguage) {
      storage.set(STORAGE_KEYS.LANGUAGE, state.currentLanguage);
    }
    
    // Update document language attribute
    document.documentElement.lang = state.currentLanguage;
    
    // Update document direction
    document.documentElement.dir = state.isRTL ? 'rtl' : 'ltr';
    
    // Add language class to body
    document.body.className = document.body.className.replace(/lang-\w+/g, '');
    document.body.classList.add(`lang-${state.currentLanguage}`);
    
    // Load Myanmar fonts if switching to Myanmar
    if (state.currentLanguage === LANGUAGES.MM && !state.fontLoaded) {
      loadMyanmarFonts();
    }
  }, [state.currentLanguage, state.preferences.persistLanguage, state.fontLoaded, loadMyanmarFonts]);
  
  // Save preferences when changed
  useEffect(() => {
    storage.set(STORAGE_KEYS.LANGUAGE_PREFERENCES, state.preferences);
  }, [state.preferences]);
  
  // Change language
  const changeLanguage = useCallback((language) => {
    if (Object.values(LANGUAGES).includes(language) && language !== state.currentLanguage) {
      dispatch({
        type: LANGUAGE_ACTIONS.SET_LANGUAGE,
        payload: language,
      });
    }
  }, [state.currentLanguage]);
  
  // Toggle between languages
  const toggleLanguage = useCallback(() => {
    const newLanguage = state.currentLanguage === LANGUAGES.EN 
      ? LANGUAGES.MM 
      : LANGUAGES.EN;
    changeLanguage(newLanguage);
  }, [state.currentLanguage, changeLanguage]);
  
  // Get text based on current language with fallback
  const getText = useCallback((textObj, fallback = '') => {
    if (!textObj) return fallback;
    
    if (typeof textObj === 'string') return textObj;
    
    if (typeof textObj === 'object') {
      // Try current language first
      const currentText = textObj[state.currentLanguage];
      if (currentText) return currentText;
      
      // Try fallback language
      const fallbackText = textObj[state.preferences.fallbackLanguage];
      if (fallbackText) return fallbackText;
      
      // Try any available language
      const availableText = Object.values(textObj).find(text => text && text.trim());
      if (availableText) return availableText;
    }
    
    return fallback;
  }, [state.currentLanguage, state.preferences.fallbackLanguage]);
  
  // Get translation from loaded translations
  const getTranslation = useCallback((key, params = {}) => {
    const translations = state.translations[state.currentLanguage] || {};
    let translation = translations[key];
    
    if (!translation) {
      // Try fallback language
      const fallbackTranslations = state.translations[state.preferences.fallbackLanguage] || {};
      translation = fallbackTranslations[key];
    }
    
    if (!translation) {
      // Return key as fallback
      translation = key;
    }
    
    // Replace parameters in translation
    if (typeof translation === 'string' && Object.keys(params).length > 0) {
      Object.keys(params).forEach(param => {
        translation = translation.replace(new RegExp(`{{${param}}}`, 'g'), params[param]);
      });
    }
    
    return translation;
  }, [state.currentLanguage, state.translations, state.preferences.fallbackLanguage]);
  
  // Format number based on current language
  const formatNumber = useCallback((number, options = {}) => {
    const locale = state.currentLanguage === LANGUAGES.MM ? 'my-MM' : 'en-US';
    return new Intl.NumberFormat(locale, options).format(number);
  }, [state.currentLanguage]);
  
  // Format date based on current language
  const formatDate = useCallback((date, options = {}) => {
    const locale = state.currentLanguage === LANGUAGES.MM ? 'my-MM' : 'en-US';
    return new Intl.DateTimeFormat(locale, options).format(new Date(date));
  }, [state.currentLanguage]);
  
  // Check if current language is Myanmar
  const isMyanmar = useCallback(() => state.currentLanguage === LANGUAGES.MM, [state.currentLanguage]);
  
  // Check if current language is English
  const isEnglish = useCallback(() => state.currentLanguage === LANGUAGES.EN, [state.currentLanguage]);
  
  // Get appropriate CSS class for text
  const getTextClass = useCallback((text) => {
    const classes = [];
    
    if (isMyanmar() || (text && /[\u1000-\u109F\uAA60-\uAA7F]/.test(text))) {
      classes.push('myanmar-text');
      if (state.fontLoaded) {
        classes.push('myanmar-font-loaded');
      }
    }
    
    if (state.isRTL) {
      classes.push('rtl-text');
    }
    
    return classes.join(' ');
  }, [isMyanmar, state.fontLoaded, state.isRTL]);
  
  // Get language direction
  const getDirection = useCallback(() => state.isRTL ? 'rtl' : 'ltr', [state.isRTL]);
  
  // Update preferences
  const updatePreferences = useCallback((newPreferences) => {
    dispatch({
      type: LANGUAGE_ACTIONS.SET_PREFERENCES,
      payload: newPreferences,
    });
  }, []);
  
  // Reset language to default
  const resetLanguage = useCallback(() => {
    dispatch({ type: LANGUAGE_ACTIONS.RESET_LANGUAGE });
  }, []);
  
  // Load additional translations
  const loadTranslations = useCallback(async (language, translations) => {
    dispatch({
      type: LANGUAGE_ACTIONS.SET_TRANSLATIONS,
      payload: { language, translations },
    });
  }, []);
  
  // Get available languages
  const getAvailableLanguages = useCallback(() => {
    return Object.keys(LANGUAGES).map(key => ({
      code: LANGUAGES[key],
      label: key === 'EN' ? 'English' : 'မြန်မာ',
      nativeLabel: key === 'EN' ? 'English' : 'မြန်မာ',
    }));
  }, []);
  
  // Context value
  const value = {
    // State
    currentLanguage: state.currentLanguage,
    isLoading: state.isLoading,
    translations: state.translations,
    isRTL: state.isRTL,
    fontLoaded: state.fontLoaded,
    preferences: state.preferences,
    
    // Actions
    changeLanguage,
    toggleLanguage,
    updatePreferences,
    resetLanguage,
    loadTranslations,
    
    // Utilities
    getText,
    getTranslation,
    formatNumber,
    formatDate,
    isMyanmar,
    isEnglish,
    getTextClass,
    getDirection,
    getAvailableLanguages,
    
    // Language constants
    LANGUAGES,
  };
  
  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook to use language context
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  
  return context;
};

// HOC for components that need language support
export const withLanguage = (Component) => {
  return function LanguageComponent(props) {
    const language = useLanguage();
    return <Component {...props} language={language} />;
  };
};

// Hook for translation with parameters
export const useTranslation = () => {
  const { getTranslation, currentLanguage } = useLanguage();
  
  const t = useCallback((key, params = {}) => {
    return getTranslation(key, params);
  }, [getTranslation]);
  
  return { t, currentLanguage };
};

// Hook for formatted text
export const useFormattedText = () => {
  const { getText, formatNumber, formatDate, currentLanguage } = useLanguage();
  
  return {
    getText,
    formatNumber,
    formatDate,
    currentLanguage,
  };
};

export default LanguageContext;